import {
  CopyOutlined,
  FileExcelOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Menu, Tooltip } from "antd";
import copy from "copy-to-clipboard";
import { ExportToCsv } from "export-to-csv";
import React from "react";

import { DiscordMembers } from "../../../types/discord_members";
import { ParsedCSVMember } from "../../lib/discordTypes";

type Props = {
  members: DiscordMembers[];
};

function ExportMenu(props: Props) {
  const { members } = props;

  /**
   * Pops a notification when clicking the "Export" menu
   *
   * @param users Discord members
   * @param type string, "members" for exporting members, "id" for copying member IDs
   */
  function notify(users: any, type: string) {
    const messages: { [key: string]: any } = {
      id: "IDs copied!",
      members: "members info exported!",
    };

    if (Notification.permission === "granted") {
      const notification = new Notification(
        `${users.length} ${messages[type]}!`
      );
    } else {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          const notification = new Notification(
            `${users.length} ${messages[type]}!`
          );
        }
      });
    }
  }

  /**
   * This function copies the table's Discord members ids to the user's clipboard
   */
  const copyIds = () => {
    const copiedMembers = members.map((member) => {
      return member.discord_id;
    });
    copy(copiedMembers.join("\r\n"));
    notify(copiedMembers, "id");
  };

  /**
   * Parses a Discord member and removes the id and discord_id properties which are not
   * relevant in the exported CSV file
   *
   * @param member : The member to parse fro CSV export
   * @returns The parsed member without the id and discord_id properties
   */
  function parseMember(member: DiscordMembers): ParsedCSVMember {
    const parsedMember: ParsedCSVMember = member;
    delete parsedMember.id;
    delete parsedMember.discord_id;
    delete parsedMember.key;
    return parsedMember;
  }

  /**
   * This function takes the table's Discord members and exports them as a CSV file
   * @param members
   */
  const exportMembers = () => {
    const csvOptions = {
      fieldSeparator: ",",
      quoteStrings: '"', // eslint-disable-line
      decimalSeparator: ".",
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
      fileName: "Dots exported members",
      // headers: ['Column 1', 'Column 2', etc...] <-- Won't work with useKeysAsHeaders present!
    };
    const membersWithTags = members.map((member) => {
      let tags: any = [];
      let discord_roles: any = [];
      const parsedMember = parseMember(member);
      if (parsedMember.tags && !parsedMember.discord_roles) {
        tags = parsedMember.tags.map((e) => e.name).join(", ");
        return {
          ...parsedMember,
          tags,
        };
      }
      if (parsedMember.tags && parsedMember.discord_roles) {
        tags = parsedMember.tags.map((e) => e.name).join(", ");
        discord_roles = parsedMember.discord_roles
          .map((e) => e.name)
          .join(", ");
        return {
          ...parsedMember,
          tags,
          discord_roles,
        };
      }
      if (!parsedMember.tags && parsedMember.discord_roles) {
        discord_roles = parsedMember.discord_roles
          .map((e) => e.name)
          .join(", ");
        return {
          ...parsedMember,
          discord_roles,
        };
      }
      return parsedMember;
    });
    const csvExporter = new ExportToCsv(csvOptions);
    csvExporter.generateCsv(membersWithTags);
    notify(members, "members");
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" icon={<FileExcelOutlined />} onClick={exportMembers}>
        Export all members to CSV
      </Menu.Item>
      <Menu.Item key="2" icon={<CopyOutlined />} onClick={copyIds}>
        Copy all Discord IDs
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown overlay={menu}>
      <Tooltip title="Pick an option below">
        <Button type="primary" size="small" icon={<SaveOutlined />}>
          Export
        </Button>
      </Tooltip>
    </Dropdown>
  );
}

export default ExportMenu;
